import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MedCurationLanding.css';
import {
  clearStoredLabelBricksToken,
  configureLabelBricksAuthSession,
  getLabelBricksAppUrl,
  getLabelBricksServerAccessStatus,
  getStoredLabelBricksToken,
  resetLabelBricksAuthSession,
  type LabelBricksServerAccessStatus,
} from './medCurationAuth';

type GateState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | {
      kind: 'blocked';
      title: string;
      message: string;
      detail?: string;
      ctaHref?: string;
      ctaLabel?: string;
    };

function MedCurationAccessGate({
  servicesManager,
  children,
}: {
  servicesManager: any;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const [gateState, setGateState] = useState<GateState>({ kind: 'loading' });
  const { userAuthenticationService } = servicesManager.services;
  const connectServerUrl = useMemo(
    () => `${getLabelBricksAppUrl()}/?tab=server-config`,
    []
  );

  useEffect(() => {
    const token = getStoredLabelBricksToken();

    const resetAndReturnToWelcome = () => {
      clearStoredLabelBricksToken();
      resetLabelBricksAuthSession(userAuthenticationService);
      navigate('/welcome', { replace: true });
    };

    if (!token) {
      resetAndReturnToWelcome();
      return;
    }

    configureLabelBricksAuthSession(token, userAuthenticationService, resetAndReturnToWelcome);

    const validateAccess = async () => {
      try {
        const accessStatus: LabelBricksServerAccessStatus =
          await getLabelBricksServerAccessStatus(token);

        if (accessStatus.canUsePlatform && accessStatus.hasServer) {
          setGateState({ kind: 'ready' });
          return;
        }

        setGateState({
          kind: 'blocked',
          title: 'Server connection required',
          message:
            accessStatus.message ||
            'This Hugging Face account is linked, but no saved server is attached in LabelBricks yet.',
          detail:
            accessStatus.labelBricksEmail
              ? `Signed in as ${accessStatus.labelBricksEmail}. Add a server in LabelBricks, then return here with the same account.`
              : 'Add a server in LabelBricks, then return here with the same Hugging Face account.',
          ctaHref: connectServerUrl,
          ctaLabel: 'Connect server in LabelBricks',
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (/invalid token|authentication required|user not found/i.test(message)) {
          resetAndReturnToWelcome();
          return;
        }

        setGateState({
          kind: 'blocked',
          title: 'Could not verify server access',
          message:
            message ||
            'Med-Curation could not verify whether this account has a saved LabelBricks server.',
          detail:
            'Check that the LabelBricks API is reachable from this app and that this account has completed Hugging Face sign-in.',
        });
      }
    };

    void validateAccess();
  }, [connectServerUrl, navigate, userAuthenticationService]);

  if (gateState.kind === 'ready') {
    return <>{children}</>;
  }

  if (gateState.kind === 'blocked') {
    return (
      <div className="mc-page">
        <div className="mc-callback">
          <div className="mc-callback-card">
            <h1 className="mc-callback-title">{gateState.title}</h1>
            <p className="mc-callback-copy">{gateState.message}</p>
            {gateState.detail ? (
              <p className="mc-callback-copy">{gateState.detail}</p>
            ) : null}
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-center">
              {gateState.ctaHref ? (
                <a
                  className="mc-btn mc-btn-hf"
                  href={gateState.ctaHref}
                >
                  {gateState.ctaLabel || 'Open LabelBricks'}
                </a>
              ) : null}
              <button
                type="button"
                className="mc-btn"
                onClick={() => {
                  clearStoredLabelBricksToken();
                  resetLabelBricksAuthSession(userAuthenticationService);
                  navigate('/welcome', { replace: true });
                }}
              >
                Back to sign-in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mc-page">
      <div className="mc-callback">
        <div className="mc-callback-card">
          <span
            className="mc-spinner"
            aria-hidden="true"
          />
          <h1 className="mc-callback-title">Checking LabelBricks access…</h1>
          <p className="mc-callback-copy">
            Verifying that this Hugging Face account already has a saved server attached in LabelBricks.
          </p>
        </div>
      </div>
    </div>
  );
}

export default MedCurationAccessGate;